/**
 * Kitchen sink-group membership queries — the twin of
 * `vanity-assembly/fixtureGroup.js` for the sink / faucet / countertop /
 * base-cabinet installation.
 *
 * Pure. Every helper takes the room's objects array and returns a member or
 * null; nothing here mutates or does I/O.
 *
 * Why this exists: a kitchen faucet's position is derived from its sink
 * (setback + facing) and its countertop (deck Y), so every consumer that
 * re-seats a faucet has to resolve both. Two copies of that lookup had
 * drifted — the cloud `style_kitchen` tool fell back to `fixtureGroupId`
 * when the faucet had no sink parent, the web model-picker did not — and
 * because `swapKitchenFaucetModel` degrades silently rather than failing
 * when the sink is missing, the web path buried swapped faucets inside the
 * countertop instead of erroring. One implementation, both callers.
 *
 * Two relationship layers meet here, and they are NOT interchangeable:
 *   `parents[]`      — anchoring: "compute my position from this object".
 *   `fixtureGroupId` — flat membership: "we move together".
 * These helpers prefer `parents` and fall back to the group, because a
 * parent names a specific host while a group id only names a set.
 */

/** A sink, but not a faucet — `type` matching mirrors the scan/layout vocab. */
function isSinkLike(obj) {
  const t = obj?.type || '';
  return /sink/i.test(t) && !/faucet/i.test(t);
}

function isCountertopLike(obj) {
  return obj?.type === 'countertop' || Boolean(obj?.category?.countertop);
}

function isKitchenFaucetLike(obj) {
  return (
    obj?.type === 'kitchen-faucet' || Boolean(obj?.category?.['kitchen-faucet'])
  );
}

/**
 * The faucet of ANY sink installation this emitter links — kitchen or
 * bathroom vocabulary.
 *
 * `emitKitchenSinkLinks` describes the geometry of "a basin in a counter over
 * a base cabinet, with a faucet behind it", and a cabinet-run's VANITY
 * profile builds exactly that shape out of the bathroom vocabulary
 * (`sink` + `bathroom-sink-faucet`, `cabinet-runs/profiles.js`). Matching
 * only `kitchen-faucet` meant the emitter saw zero faucets on a vanity run
 * and returned NO links at all — the group opened empty in the Group Editor
 * and a basin drag left its faucet behind, while the identical kitchen run
 * got its `rp` link.
 *
 * Deliberately NOT routed through `vanity-assembly`'s emitter instead: that
 * one is for a vanity ASSEMBLY (mirror / sconces / overhead), and its
 * `getFixtureGroupSinks` counts a run's sink-base cabinet as a sink — the
 * category key `sinkBase` substring-matches `sink` — which pairs the faucet
 * to the cabinet's centreline rather than the basin's.
 */
function isSinkFaucetLike(obj) {
  return (
    isKitchenFaucetLike(obj) ||
    obj?.type === 'bathroom-sink-faucet' ||
    Boolean(obj?.category?.['bathroom-sink-faucet'])
  );
}

// Anchor preference for a sink installation, most structural first. Mirrors
// the group editor's PREFERRED_ANCHOR_TYPES so the links we stamp at build
// time hang off the object the editor will resolve when the user opens the
// group — a mismatch leaves the links invisible. `sink` is the bathroom
// vocabulary's basin (a vanity-profile cabinet run), last for the same reason
// `kitchen-sink` is: the base cabinet anchors whenever there is one.
const ANCHOR_TYPES = ['lower-cabinet', 'kitchen-sink', 'sink'];

/**
 * The most structural member of a kitchen sink group: its base cabinet, else
 * the basin itself. Returns null for a group with neither.
 *
 * @param {object[]} groupObjects — one fixture group's objects.
 * @returns {object|null}
 */
function getKitchenSinkAnchor(groupObjects) {
  if (!Array.isArray(groupObjects)) return null;
  for (const type of ANCHOR_TYPES) {
    const match = groupObjects.find((o) => o?.type === type);
    if (match) return match;
  }
  return null;
}

/**
 * The kitchen sink a faucet belongs to: its `kitchen-sink` parent, else a
 * sink sharing its `fixtureGroupId`.
 *
 * @param {object} faucet
 * @param {object[]} objects — the room's objects.
 * @returns {object|null}
 */
function findKitchenSinkFor(faucet, objects) {
  if (!faucet || !Array.isArray(objects)) return null;
  const sinkParent = (faucet.parents || []).find((p) =>
    /sink/i.test(p?.type || '')
  );
  if (sinkParent) {
    const byParent = objects.find((o) => o?.id === sinkParent.id);
    if (byParent) return byParent;
  }
  if (!faucet.fixtureGroupId) return null;
  return (
    objects.find(
      (o) =>
        o?.fixtureGroupId &&
        o.fixtureGroupId === faucet.fixtureGroupId &&
        isSinkLike(o)
    ) || null
  );
}

/**
 * The countertop a kitchen fixture sits on. Checks each candidate's
 * `parents` in order — pass the faucet first and its sink second, so a
 * faucet with no countertop parent still resolves through the sink it sits
 * behind — then falls back to a countertop sharing a `fixtureGroupId`.
 *
 * @param {object[]} objects — the room's objects.
 * @param {...object} candidates — objects whose parents to consult, in
 *   preference order.
 * @returns {object|null}
 */
function findKitchenCountertopFor(objects, ...candidates) {
  if (!Array.isArray(objects)) return null;
  for (const candidate of candidates) {
    const ctParent = (candidate?.parents || []).find(
      (p) => p?.type === 'countertop'
    );
    if (!ctParent) continue;
    const byParent = objects.find((o) => o?.id === ctParent.id);
    if (byParent) return byParent;
  }
  for (const candidate of candidates) {
    if (!candidate?.fixtureGroupId) continue;
    const byGroup = objects.find(
      (o) =>
        o?.fixtureGroupId &&
        o.fixtureGroupId === candidate.fixtureGroupId &&
        isCountertopLike(o)
    );
    if (byGroup) return byGroup;
  }
  return null;
}

module.exports = {
  isSinkLike,
  isCountertopLike,
  isKitchenFaucetLike,
  isSinkFaucetLike,
  getKitchenSinkAnchor,
  ANCHOR_TYPES,
  findKitchenSinkFor,
  findKitchenCountertopFor,
};
